import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert } from "@/integrations/supabase/types";
import type { ContentVoiceProfile, ContentAvatar } from "../types";
import { getFriendlyMessage, normalizeText } from "../_shared";
import {
  serializeAuthenticityMarkerRows,
  serializeAvoidTermRows,
  serializeBuildingBlockProtocolRows,
  serializeHighFrequencyTermRows,
  serializeMisrepresentationRows,
  serializeParagraphFlowRows,
  serializePeakExemplarRows,
  serializeRedLineRows,
  serializeRhetoricalRows,
  serializeSentenceArchitectureRows,
  serializeSignaturePhraseRows,
  serializeSynthesisRows,
  serializeVerbalMarkerRows,
  type AuthenticityMarkerRow,
  type AvoidTermRow,
  type BuildingBlockProtocolRow,
  type HighFrequencyTermRow,
  type MisrepresentationRow,
  type ParagraphFlowRow,
  type PeakExemplarRow,
  type RedLineRow,
  type RhetoricalMoveRow,
  type SentenceArchitectureRow,
  type SignaturePhraseRow,
  type SynthesisRow,
  type VerbalMarkerRow,
} from "@/components/composer/panels/voiceProfilePanelData";

export type VoiceProfileInput = Partial<{
  title: string;
  dual_voice_modes_enabled: boolean;
  // Free-form text
  phrase_preferences: string;
  avoid_list: AvoidTermRow[] | string;
  // Aethyrius-parity extensions
  core_identity: string;
  voice_mode_a_label: string;
  voice_mode_b_label: string;
  voice_mode_a_notes: string;
  voice_mode_b_notes: string;
  sentence_architecture: SentenceArchitectureRow[] | string;
  paragraph_flow: ParagraphFlowRow[] | string;
  vocabulary_fingerprint: HighFrequencyTermRow[] | string;
  signature_phrases: SignaturePhraseRow[] | string;
  verbal_markers: VerbalMarkerRow[] | string;
  rhetorical_moves: RhetoricalMoveRow[] | string;
  primary_tone: string;
  secondary_tone: string;
  formality_position: string;
  emotional_register: string;
  tradition_synthesis: SynthesisRow[] | string;
  red_lines: RedLineRow[] | string;
  common_misrepresentations: MisrepresentationRow[] | string;
  peak_author_examples: PeakExemplarRow[] | string;
  authenticity_markers: AuthenticityMarkerRow[] | string;
  lexical_compliance: string;
  building_block_protocols: BuildingBlockProtocolRow[] | string;
}>;

export const saveVoiceProfile = async (
  workspaceId: string,
  values: VoiceProfileInput,
  voiceProfileId?: string,
) => {
  const basePayload = {
    title: normalizeText(values.title ?? "Master voice profile"),
    ...(typeof values.dual_voice_modes_enabled === "boolean"
      ? { dual_voice_modes_enabled: values.dual_voice_modes_enabled }
      : {}),
  };


  const query = voiceProfileId
    ? supabase.from("content_voice_profiles").update(basePayload).eq("id", voiceProfileId)
    : supabase.from("content_voice_profiles").insert({ workspace_id: workspaceId, is_primary: true, ...basePayload } as TablesInsert<"content_voice_profiles">);

  const { data, error } = await query.select("*").single();
  if (error) throw new Error(getFriendlyMessage(error, "Failed to save the voice profile"));

  const saved = data as ContentVoiceProfile;
  const t = (v?: string) => (v && v.trim().length > 0 ? v.trim() : null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- extended RPC signature, types regenerate after migration apply
  const { error: textError } = await (supabase as any).rpc("set_voice_profile_text", {
    _voice_id: saved.id,
    _avoid_list: t(
      Array.isArray(values.avoid_list)
        ? serializeAvoidTermRows(values.avoid_list)
        : values.avoid_list,
    ),
    _phrase_preferences: t(values.phrase_preferences),
    _core_identity: t(values.core_identity),
    _voice_mode_a_label: t(values.voice_mode_a_label),
    _voice_mode_b_label: t(values.voice_mode_b_label),
    _voice_mode_a_notes: t(values.voice_mode_a_notes),
    _voice_mode_b_notes: t(values.voice_mode_b_notes),
    _sentence_architecture: t(
      Array.isArray(values.sentence_architecture)
        ? serializeSentenceArchitectureRows(values.sentence_architecture)
        : values.sentence_architecture,
    ),
    _paragraph_flow: t(
      Array.isArray(values.paragraph_flow)
        ? serializeParagraphFlowRows(values.paragraph_flow)
        : values.paragraph_flow,
    ),
    _vocabulary_fingerprint: t(
      Array.isArray(values.vocabulary_fingerprint)
        ? serializeHighFrequencyTermRows(values.vocabulary_fingerprint)
        : values.vocabulary_fingerprint,
    ),
    _signature_phrases: t(
      Array.isArray(values.signature_phrases)
        ? serializeSignaturePhraseRows(values.signature_phrases)
        : values.signature_phrases,
    ),
    _verbal_markers: t(
      Array.isArray(values.verbal_markers)
        ? serializeVerbalMarkerRows(values.verbal_markers)
        : values.verbal_markers,
    ),
    _rhetorical_moves: t(
      Array.isArray(values.rhetorical_moves)
        ? serializeRhetoricalRows(values.rhetorical_moves)
        : values.rhetorical_moves,
    ),
    _primary_tone: t(values.primary_tone),
    _secondary_tone: t(values.secondary_tone),
    _formality_position: t(values.formality_position),
    _emotional_register: t(values.emotional_register),
    _tradition_synthesis: t(
      Array.isArray(values.tradition_synthesis)
        ? serializeSynthesisRows(values.tradition_synthesis)
        : values.tradition_synthesis,
    ),
    _red_lines: t(
      Array.isArray(values.red_lines)
        ? serializeRedLineRows(values.red_lines)
        : values.red_lines,
    ),
    _common_misrepresentations: t(
      Array.isArray(values.common_misrepresentations)
        ? serializeMisrepresentationRows(values.common_misrepresentations)
        : values.common_misrepresentations,
    ),
    _peak_author_examples: t(
      Array.isArray(values.peak_author_examples)
        ? serializePeakExemplarRows(values.peak_author_examples)
        : values.peak_author_examples,
    ),
    _authenticity_markers: t(
      Array.isArray(values.authenticity_markers)
        ? serializeAuthenticityMarkerRows(values.authenticity_markers)
        : values.authenticity_markers,
    ),
    _lexical_compliance: t(values.lexical_compliance),
    _building_block_protocols: t(
      Array.isArray(values.building_block_protocols)
        ? serializeBuildingBlockProtocolRows(values.building_block_protocols)
        : values.building_block_protocols,
    ),
  });
  if (textError) throw new Error(getFriendlyMessage(textError, "Failed to save the Voice document text"));

  return saved;
};

export const createAvatar = async (workspaceId: string, values: {
  name: string;
  experienceLevel?: string;
  summary?: string;
  painPoints?: string[];
  desires?: string[];
  toneGuidance?: string;
  vocabularyAssumptions?: string;
  messagingPosture?: string;
  voiceModeAWeight?: number;
  toneAdjustments?: string;
  vocabularyAdjustments?: string;
}) => {
  const clampedWeight = Math.max(0, Math.min(100, Math.round(values.voiceModeAWeight ?? 50)));
  const payload: TablesInsert<"content_avatars"> = {
    workspace_id: workspaceId,
    name: normalizeText(values.name),
    experience_level: values.experienceLevel?.trim() ?? null,
    pain_points: values.painPoints?.map((item) => item.trim()).filter(Boolean) ?? [],
    desires: values.desires?.map((item) => item.trim()).filter(Boolean) ?? [],
    tone_guidance: values.toneGuidance?.trim() ?? null,
    vocabulary_assumptions: values.vocabularyAssumptions?.trim() ?? null,
    messaging_posture: values.messagingPosture?.trim() ?? null,
    voice_mode_a_weight: clampedWeight,
    tone_adjustments: values.toneAdjustments?.trim() ?? null,
    vocabulary_adjustments: values.vocabularyAdjustments?.trim() ?? null,
  };
  const { data, error } = await supabase.from("content_avatars").insert(payload).select("*").single();
  if (error) throw new Error(getFriendlyMessage(error, "Failed to create the avatar"));

  const saved = data as ContentAvatar;
  const summaryVal = values.summary?.trim() ?? null;
  if (summaryVal) {
    await supabase.rpc("set_content_avatar_text", {
      _avatar_id: saved.id,
      _summary: summaryVal,
      _notes: null,
    });
  }

  return saved;
};

export type AvatarUpdateInput = Partial<{
  name: string;
  experienceLevel: string;
  summary: string;
  painPoints: string[];
  desires: string[];
  toneGuidance: string;
  vocabularyAssumptions: string;
  messagingPosture: string;
  voiceModeAWeight: number;
  toneAdjustments: string;
  vocabularyAdjustments: string;
}>;

export const updateAvatar = async (avatarId: string, values: AvatarUpdateInput) => {
  const payload: Record<string, unknown> = {};
  if (values.name !== undefined) payload.name = normalizeText(values.name);
  if (values.experienceLevel !== undefined) payload.experience_level = values.experienceLevel.trim() || null;
  if (values.painPoints !== undefined) payload.pain_points = values.painPoints.map((item) => item.trim()).filter(Boolean);
  if (values.desires !== undefined) payload.desires = values.desires.map((item) => item.trim()).filter(Boolean);
  if (values.toneGuidance !== undefined) payload.tone_guidance = values.toneGuidance.trim() || null;
  if (values.vocabularyAssumptions !== undefined) payload.vocabulary_assumptions = values.vocabularyAssumptions.trim() || null;
  if (values.messagingPosture !== undefined) payload.messaging_posture = values.messagingPosture.trim() || null;
  if (values.toneAdjustments !== undefined) payload.tone_adjustments = values.toneAdjustments.trim() || null;
  if (values.vocabularyAdjustments !== undefined) payload.vocabulary_adjustments = values.vocabularyAdjustments.trim() || null;
  if (values.voiceModeAWeight !== undefined) {
    payload.voice_mode_a_weight = Math.max(0, Math.min(100, Math.round(values.voiceModeAWeight)));
  }

  if (Object.keys(payload).length > 0) {
    const { error } = await supabase.from("content_avatars").update(payload as never).eq("id", avatarId);
    if (error) throw new Error(getFriendlyMessage(error, "Failed to update the avatar"));
  }

  if (values.summary !== undefined) {
    const summaryVal = values.summary.trim() || null;
    const { error: rpcError } = await supabase.rpc("set_content_avatar_text", {
      _avatar_id: avatarId,
      _summary: summaryVal as unknown as string,
      _notes: null as unknown as string,
    });
    if (rpcError) throw new Error(getFriendlyMessage(rpcError, "Failed to update the avatar summary"));
  }
};

export const deleteAvatar = async (avatarId: string) => {
  const { error } = await supabase.from("content_avatars").delete().eq("id", avatarId);
  if (error) throw new Error(getFriendlyMessage(error, "Failed to delete the avatar"));
};

export const reorderAvatars = async (workspaceId: string, orderedIds: string[]) => {
  const results = await Promise.all(
    orderedIds.map((id, index) =>
      supabase
        .from("content_avatars")
        .update({ sort_order: index })
        .eq("id", id)
        .eq("workspace_id", workspaceId),
    ),
  );
  const firstError = results.find((r) => r.error)?.error;
  if (firstError) throw new Error(getFriendlyMessage(firstError, "Failed to reorder avatars"));
};
